/**
 * lazyRoute() - the ONLY way App.tsx (and the two other route-shaped lazy
 * spots, Landing.tsx and AssistantLauncher.tsx) may lazy-load a component.
 * Sentry proved on staging (2026-07-30 through 2026-08-13) that a bare
 * React.lazy() call still crashes into the error boundary when a deploy has
 * replaced the hashed chunk a stale tab is asking for, even though this app
 * already had a stale-chunk reload handler. 30 crash events across
 * /bulletins, /, /class/:id, /dancers/... in 14 days, plus a 46-event
 * "stale-chunk reload" counter that clearly wasn't preventing them.
 *
 * DIAGNOSIS (proven by reading the actual shipped code, not guessed at -
 * see hub/node_modules/vite/dist/node/chunks/node.js's `preload()` /
 * `handlePreloadError()`, cross-checked against a real `vite build`'s
 * dist/assets/index-*.js, and hub/node_modules/react/cjs/react.production.min.js's
 * minified lazyInitializer, function T). Two compounding bugs, not one:
 *
 * 1. React.lazy's OWN import() rejection DOES go through vite:preloadError
 *    (this repo's Vite/rolldown build wraps every dynamic import() - not
 *    just ones with preload dependencies - through the same `preload()`
 *    helper that dispatches this event; verified directly in the built
 *    output). But main.tsx's old listener calling `event.preventDefault()`
 *    on that event did not mean "retry" or "this import will now fail
 *    cleanly" - it meant "Vite, do not throw," and Vite's `handlePreloadError`
 *    responds to that by letting its `.catch()` handler return normally
 *    (implicitly `undefined`), so the WHOLE import "succeeds" with a
 *    resolved value of `undefined` instead of a real module or a rejection.
 *    React's lazyInitializer then runs `payload._result.default` with NO
 *    defensive check in the production build (`if(1===a._status)return
 *    a._result.default;` - react.production.min.js, function T) - and
 *    `_result` is `undefined`. That is EXACTLY "TypeError: Cannot read
 *    properties of undefined (reading 'default')" in Chrome and "undefined
 *    is not an object (evaluating 'e._result.default')" in Safari, the two
 *    signatures Sentry captured. The "stale-chunk reload" info event and the
 *    crash are ~10ms apart because they are nearly the SAME event: reload()
 *    was called, but it does not synchronously tear down the tab, so React
 *    gets to finish resolving the now-corrupted lazy payload first.
 *
 * 2. Fixing #1 naively - having this wrapper also call
 *    reloadGuard.ts's guard-and-reload sequence from its OWN catch block -
 *    is not enough on its own, and was proven not enough with a probe before
 *    this file was written: main.tsx's listener fires SYNCHRONOUSLY, nested
 *    INSIDE the very `await factory()` call below (Vite dispatches
 *    vite:preloadError, and only returns control to this file's `catch`
 *    AFTER that dispatch, once the resulting promise settles). A global
 *    listener therefore ALWAYS reaches reloadGuard's one-shot cooldown
 *    first, every single time, not just on a genuine second failure - which
 *    left this wrapper's own attempt permanently cooldown-blocked and
 *    rethrowing on the very first failure it ever saw. The fix is
 *    consolidation, not addition: main.tsx no longer listens for
 *    vite:preloadError at all (see its comment), and this wrapper is the
 *    ONLY caller of reloadGuard.ts's triggerStaleChunkReload(). With nothing
 *    else calling preventDefault(), Vite always rejects on a stale chunk
 *    (never resolves to undefined) and this file's `catch` is always the
 *    first and only responder.
 *
 * Both were proven by execution (temp Node probes reproducing the exact
 * promise/event sequence with the real reloadGuard.ts, a faithful copy of
 * Vite's preload()/handlePreloadError(), and a fake window), not by reading
 * the code and guessing - see the PR description for the probe output.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
// Explicit .ts extension (allowImportingTsExtensions in tsconfig.json):
// loadRouteModule() below is imported directly by node:test under
// --experimental-strip-types for its bite tests, and that loader will not
// resolve an extensionless relative specifier. Same rule as lib/alternates.ts.
import { holdStaleChunkReload, triggerStaleChunkReload } from './reloadGuard.ts';
import { hasRegisteredUnsavedWork, uploadInFlight } from './unsavedWork.ts';
import { domHasUnsavedText } from './unsavedWorkDom.ts';

/**
 * Would a reload right now throw something away? The same question the
 * version auto-update asks (lib/freshness.ts): a page that warns before you
 * leave (the unsaved-work registry, which lib/useLeaveWarning.ts fills), an
 * upload still sending, or words typed in a box. The open-dialog check is
 * left out on purpose: the Assistant panel, the one part loaded on demand on
 * top of a page, is itself a dialog, so it would hold every time.
 */
function reloadWouldLoseWork(): boolean {
  return hasRegisteredUnsavedWork() || uploadInFlight() || domHasUnsavedText();
}

/**
 * The testable core: wraps a React.lazy() factory so a stale-chunk failure
 * can never resolve to (or throw) something React's lazy resolution will
 * choke on. Exported (rather than kept private to lazyRoute() below) so
 * node:test can drive it directly with a fake rejecting factory instead of
 * needing a real Suspense tree - see tests/lazy-route-recovery.test.mjs.
 *
 * - Success: resolves exactly like the original factory would.
 * - Failure, reload permitted: triggers the SAME guarded reload
 *   main.tsx used to (reloadGuard.ts's triggerStaleChunkReload - one Sentry
 *   breadcrumb, one cooldown stamp, one window.location.reload()) and
 *   returns a promise that NEVER settles. React only ever reads this
 *   promise from inside a Suspense boundary, so a promise that hangs forever
 *   just leaves that boundary showing its loading fallback - not a crash -
 *   for the brief window before the reload actually tears the tab down.
 *   Resolving to a placeholder module instead was considered and rejected:
 *   that risks React mounting something artificial mid-reload, where never
 *   settling cannot race anything because nothing downstream ever runs.
 * - Failure while a reload would throw something away (unsaved work, an
 *   upload still sending, words typed in a box) and the update pill is on
 *   screen: no reload. The hold is recorded (reloadGuard.ts), the pill shows
 *   An update is ready with its Reload, and this promise never settles, so
 *   the part waits on its loading screen until the person taps Reload. This
 *   comes first, ahead of the cooldown below too: rethrowing would take the
 *   page down to the error screen and lose the same work.
 * - Failure, reload blocked by the cooldown: a reload already happened
 *   recently and this tab is STILL failing to load the same (or another)
 *   chunk - a genuinely broken chunk or a truly offline network, not routine
 *   post-deploy staleness. Rethrows the ORIGINAL error so the boundary shows
 *   its real screen (with its own manual Reload button) instead of hanging
 *   forever on a fallback that will never resolve.
 */
export async function loadRouteModule<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  try {
    return await factory();
  } catch (error) {
    // Something a reload would lose, and the update pill on screen to offer
    // the reload instead (train follow-up, 2026-10-24; reloadGuard.ts's
    // stale-chunk hold): wait for the person's tap, the same as the version
    // auto-update. This part keeps its loading screen meanwhile. With no
    // pill to offer it, the hold refuses and the reload goes as before.
    if (reloadWouldLoseWork() && holdStaleChunkReload()) {
      return new Promise<{ default: T }>(() => {
        // Never settles, for the same reason as below. Only a reload brings
        // this part back, and the pill offers it.
      });
    }
    if (triggerStaleChunkReload()) {
      return new Promise<{ default: T }>(() => {
        // Deliberately never resolves or rejects - see doc comment above.
      });
    }
    throw error;
  }
}

/**
 * Drop-in replacement for React's own `lazy()`, used for every route-shaped
 * dynamic import in this app (App.tsx's routes, Landing.tsx's role-based
 * homes, AssistantLauncher's chat panel). Same call shape as `lazy()` -
 * `lazyRoute(() => import('./pages/X'))`, including the
 * `.then((m) => ({ default: m.Named }))` remap some call sites use for a
 * named export - so converting a call site is a pure `lazy(` -> `lazyRoute(`
 * rename with no other changes.
 */
export function lazyRoute<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> & RoutePreload {
  // preload() fetches the same chunk the page will ask for, ahead of the tap,
  // through preloadChunk(), so a failed fetch here never reloads anything
  // (lib/routeWarm.ts is its caller). The browser's module map hands the
  // page's own import() the already-loaded module afterwards.
  return Object.assign(lazy(() => loadRouteModule(factory)), {
    preload: () => preloadChunk(factory),
  });
}

/** A route component whose chunk can be fetched ahead of need. */
export interface RoutePreload {
  preload: () => Promise<boolean>;
}

/**
 * Fetch a lazy component's chunk AHEAD of need, for a component nobody is
 * waiting on yet (GlobalChrome's menu drawer). Resolves true once the chunk
 * has loaded and false if it could not, and never rejects. Above all it
 * never calls triggerStaleChunkReload(): a fetch nobody asked for must not
 * reload the page out from under a person mid-task (the hazard lazyOptional()
 * below describes). Mount the component through lazyRoute() only once this
 * says true, or once a person has actually asked for it.
 */
export function preloadChunk(factory: () => Promise<unknown>): Promise<boolean> {
  let attempt: Promise<unknown>;
  try {
    attempt = factory();
  } catch {
    return Promise.resolve(false);
  }
  return Promise.resolve(attempt).then(
    () => true,
    () => false
  );
}

/**
 * The testable core of lazyOptional() below: the factory's own answer,
 * success or failure, and NOTHING else. In particular it never calls
 * triggerStaleChunkReload(), which is the whole point. Exported for
 * tests/lazy-route-recovery.test.mjs, same reason as loadRouteModule.
 */
export async function loadOptionalModule<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  return factory();
}

/**
 * lazy() for a component the Hub can do without, and ONLY for one rendered
 * inside an OptionalFeatureBoundary (components/OptionalFeatureBoundary.tsx).
 * Written for PushSync and PushOptInGate (PR #1242 refute round, P3).
 *
 * lazyRoute() is the wrong tool for those. On a failed chunk it reloads the
 * WHOLE Hub, and it spends reloadGuard's 60 second cooldown doing it, so a
 * notification listener nobody is waiting on could reload the page out from
 * under a person mid-task and then leave a real route failure a minute later
 * with no automatic recovery. Here a failed chunk simply rejects, React
 * hands the error to the nearest boundary, and the OptionalFeatureBoundary
 * around it renders nothing and reports it. The person keeps the Hub; the
 * next route that needs a fresh chunk still gets its stale-chunk reload.
 *
 * NEVER use this for a route or for anything a person is waiting to see:
 * outside an OptionalFeatureBoundary its failure reaches AppErrorBoundary,
 * the exact crash lazyRoute() exists to prevent.
 * tests/lazy-route-contract.test.mjs pins where it may be used.
 */
export function lazyOptional<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() => loadOptionalModule(factory));
}
